import React, { useState, useEffect, useMemo } from 'react';
import { CustomerWorkspace, CheckoutAddon, PlanDetail } from '../../types';
import {
  Layers,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Table,
  LayoutGrid,
  Search,
  Check,
  Server,
  Building2,
  Sparkles,
} from 'lucide-react';

interface PortalWorkspacesProps {
  customerId: string;
  subscriptionId?: string;
  plans: PlanDetail[];
}

type SubViewMode = 'allocated-list' | 'matrix' | 'workspace-detail';

export const PortalWorkspaces: React.FC<PortalWorkspacesProps> = ({
  customerId,
  subscriptionId,
  plans,
}) => {
  const [workspaces, setWorkspaces] = useState<CustomerWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [subView, setSubView] = useState<SubViewMode>('allocated-list');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkspaceFilter, setSelectedWorkspaceFilter] = useState<string>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);

  // Available add-ons from plans/system
  const allAddons: CheckoutAddon[] = useMemo(() => {
    const addonsMap = new Map<string, CheckoutAddon>();
    plans.forEach(plan => {
      (plan.addons || []).forEach(addon => {
        if (!addonsMap.has(addon.id)) {
          addonsMap.set(addon.id, addon);
        }
      });
    });
    // Fallback default workspace add-ons if none configured on plans
    if (addonsMap.size === 0) {
      const defaults: CheckoutAddon[] = [
        { id: 'addon-dedicated-ip', name: 'Dedicated IP & Proxy', price: 1499, currency: 'INR', description: 'Dedicated static outbound IP address for this workspace', isEnabled: true, isPerWorkspace: true },
        { id: 'addon-audit-vault', name: 'HIPAA / SOC2 Audit Vault', price: 2999, currency: 'INR', description: 'Immutable 7-year event logging & cryptographic audit trail', isEnabled: true, isPerWorkspace: true },
        { id: 'addon-ai-powerpack', name: 'AI Reasoning Accelerator', price: 1999, currency: 'INR', description: '10,000 workspace-scoped execution tokens & low-latency queue', isEnabled: true, isPerWorkspace: true },
        { id: 'addon-priority-sla', name: '99.99% Custom SLA', price: 4999, currency: 'INR', description: 'Under 15-minute dedicated response SLA for this workspace', isEnabled: true, isPerWorkspace: true },
      ];
      defaults.forEach(d => addonsMap.set(d.id, d));
    }
    return Array.from(addonsMap.values());
  }, [plans]);

  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/v1/customers/${customerId}/workspaces`);
      if (res.ok) {
        const data: CustomerWorkspace[] = await res.json();
        setWorkspaces(data);
        if (data.length > 0 && !selectedWorkspaceId) {
          setSelectedWorkspaceId(data[0].id);
        }
      } else {
        // Fallback default workspaces
        const initial: CustomerWorkspace[] = [
          {
            id: `ws_${customerId}_prod`,
            name: 'Production Workspace (Primary)',
            slug: 'in-prod',
            customerId,
            subscriptionId,
            isPrimary: true,
            createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
            activeAddons: [
              { addonId: 'addon-dedicated-ip', name: 'Dedicated IP & Proxy', price: 1499, currency: 'INR', assignedAt: new Date(Date.now() - 25 * 86400000).toISOString() },
              { addonId: 'addon-audit-vault', name: 'HIPAA / SOC2 Audit Vault', price: 2999, currency: 'INR', assignedAt: new Date(Date.now() - 12 * 86400000).toISOString() },
            ],
            memberCount: 8,
            environment: 'production'
          },
          {
            id: `ws_${customerId}_eu`,
            name: 'Operations & Compliance',
            slug: 'ops-compliance',
            customerId,
            subscriptionId,
            isPrimary: false,
            createdAt: new Date(Date.now() - 15 * 86400000).toISOString(),
            activeAddons: [
              { addonId: 'addon-audit-vault', name: 'HIPAA / SOC2 Audit Vault', price: 2999, currency: 'INR', assignedAt: new Date(Date.now() - 10 * 86400000).toISOString() },
            ],
            memberCount: 4,
            environment: 'production'
          },
          {
            id: `ws_${customerId}_staging`,
            name: 'Staging & Sandbox QA',
            slug: 'staging-sandbox',
            customerId,
            subscriptionId,
            isPrimary: false,
            createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
            activeAddons: [
              { addonId: 'addon-ai-powerpack', name: 'AI Reasoning Accelerator', price: 1999, currency: 'INR', assignedAt: new Date(Date.now() - 2 * 86400000).toISOString() },
            ],
            memberCount: 2,
            environment: 'staging'
          }
        ];
        setWorkspaces(initial);
        setSelectedWorkspaceId(initial[0].id);
      }
    } catch (err) {
      console.error('Failed to load workspaces', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspaces();
  }, [customerId]);

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;

    try {
      const res = await fetch(`/api/v1/customers/${customerId}/workspaces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newWorkspaceName.trim(),
          subscriptionId,
        }),
      });

      if (res.ok) {
        const newWs = await res.json();
        setWorkspaces(prev => [...prev, newWs]);
        setSelectedWorkspaceId(newWs.id);
        setNewWorkspaceName('');
        setIsCreating(false);
        setActionMessage({ type: 'success', text: `Workspace "${newWs.name}" provisioned successfully.` });
      } else {
        const err = await res.json();
        setActionMessage({ type: 'error', text: err.error || 'Failed to create workspace.' });
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: 'Error connecting to workspace service.' });
    }
    setTimeout(() => setActionMessage(null), 4000);
  };

  const handleToggleAddonForWorkspace = async (workspaceId: string, addon: CheckoutAddon) => {
    const ws = workspaces.find(w => w.id === workspaceId);
    if (!ws) return;

    const isAssigned = ws.activeAddons.some(a => a.addonId === addon.id);
    setIsPurchasing(true);

    try {
      const endpoint = `/api/v1/workspaces/${workspaceId}/addons`;
      const res = await fetch(endpoint, {
        method: isAssigned ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          addonId: addon.id,
          name: addon.name,
          price: addon.price,
          currency: addon.currency || 'USD',
        }),
      });

      if (res.ok) {
        const updatedWs = await res.json();
        setWorkspaces(prev => prev.map(w => w.id === workspaceId ? updatedWs : w));
        setActionMessage({
          type: 'success',
          text: isAssigned
            ? `Removed "${addon.name}" from ${ws.name}.`
            : `Purchased and attached "${addon.name}" to ${ws.name} ($${addon.price}/mo).`
        });
      } else {
        const err = await res.json();
        setActionMessage({ type: 'error', text: err.error || 'Failed to update workspace add-on.' });
      }
    } catch (err: any) {
      // Fallback local update if network is unavailable
      const nextAddons = isAssigned
        ? ws.activeAddons.filter(a => a.addonId !== addon.id)
        : [...ws.activeAddons, {
            addonId: addon.id,
            name: addon.name,
            price: addon.price,
            currency: addon.currency || 'USD',
            assignedAt: new Date().toISOString()
          }];
      const updatedWs = { ...ws, activeAddons: nextAddons };
      setWorkspaces(prev => prev.map(w => w.id === workspaceId ? updatedWs : w));
      setActionMessage({
        type: 'success',
        text: isAssigned
          ? `Removed "${addon.name}" from ${ws.name}.`
          : `Attached "${addon.name}" to ${ws.name}.`
      });
    } finally {
      setIsPurchasing(false);
      setTimeout(() => setActionMessage(null), 4000);
    }
  };

  // Flat list of all allocated add-ons for the table/list sub-view
  const allAllocations = useMemo(() => {
    const list: Array<{
      workspaceId: string;
      workspaceName: string;
      workspaceSlug: string;
      isPrimary: boolean;
      addonId: string;
      name: string;
      price: number;
      currency: string;
      assignedAt: string;
    }> = [];

    workspaces.forEach(ws => {
      (ws.activeAddons || []).forEach(addon => {
        list.push({
          workspaceId: ws.id,
          workspaceName: ws.name,
          workspaceSlug: ws.slug || ws.id,
          isPrimary: ws.isPrimary,
          addonId: addon.addonId,
          name: addon.name,
          price: addon.price,
          currency: addon.currency || 'USD',
          assignedAt: addon.assignedAt,
        });
      });
    });

    return list;
  }, [workspaces]);

  // Filtered allocations based on search and workspace filter
  const filteredAllocations = useMemo(() => {
    return allAllocations.filter(item => {
      const matchWs = selectedWorkspaceFilter === 'all' || item.workspaceId === selectedWorkspaceFilter;
      const matchSearch = searchQuery.trim() === '' ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.workspaceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.addonId.toLowerCase().includes(searchQuery.toLowerCase());
      return matchWs && matchSearch;
    });
  }, [allAllocations, selectedWorkspaceFilter, searchQuery]);

  // Aggregate stats
  const totalMonthlySpend = useMemo(() => {
    return allAllocations.reduce((sum, item) => sum + (item.price || 0), 0);
  }, [allAllocations]);

  const selectedWorkspace = workspaces.find(w => w.id === selectedWorkspaceId) || workspaces[0];

  if (loading) {
    return (
      <div className="p-16 text-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loading Workspace Add-ons...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Top Banner & KPI Header */}
      <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-3xl shrink-0">
              <Layers className="w-8 h-8" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase">
                  Workspace Add-on Allocations
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                  Per-Workspace Scoped
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1 max-w-2xl">
                Inspect and control dedicated licenses attached to each individual workspace instance. Add-ons are billed independently per linked tenant.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsCreating(true)}
              className="px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-sm transition-all shrink-0"
            >
              <Plus className="w-4 h-4" />
              Provision Workspace
            </button>
          </div>
        </div>

        {/* Aggregate KPI Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/80">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">
              Total Workspaces
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-slate-900 dark:text-white">{workspaces.length}</span>
              <span className="text-xs text-slate-400 font-medium">registered tenants</span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/80">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">
              Active Add-on Subscriptions
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400">{allAllocations.length}</span>
              <span className="text-xs text-slate-400 font-medium">allocated licenses</span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/80">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">
              Workspace Add-on Total
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">${totalMonthlySpend}</span>
              <span className="text-xs text-slate-400 font-medium">/ month billed</span>
            </div>
          </div>
        </div>

        {/* Sub-View Navigation Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
          <div className="flex p-1 bg-slate-100 dark:bg-slate-800 rounded-2xl w-fit">
            <button
              onClick={() => setSubView('allocated-list')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${
                subView === 'allocated-list'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Table className="w-3.5 h-3.5" />
              Active Add-ons by Workspace ({allAllocations.length})
            </button>

            <button
              onClick={() => setSubView('matrix')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${
                subView === 'matrix'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Cross-Workspace Matrix
            </button>

            <button
              onClick={() => setSubView('workspace-detail')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${
                subView === 'workspace-detail'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              Manage & Catalog
            </button>
          </div>

          {subView === 'allocated-list' && (
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filter add-ons..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 w-40 sm:w-48"
                />
              </div>

              <select
                value={selectedWorkspaceFilter}
                onChange={e => setSelectedWorkspaceFilter(e.target.value)}
                className="px-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 outline-none cursor-pointer"
              >
                <option value="all">All Workspaces ({workspaces.length})</option>
                {workspaces.map(ws => (
                  <option key={ws.id} value={ws.id}>
                    {ws.name} ({ws.activeAddons.length})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {actionMessage && (
        <div
          className={`p-4 rounded-2xl flex items-center gap-3 text-xs font-bold animate-in fade-in ${
            actionMessage.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
              : 'bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'
          }`}
        >
          {actionMessage.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0" />
          )}
          <span>{actionMessage.text}</span>
        </div>
      )}

      {/* Provision Workspace Modal Form */}
      {isCreating && (
        <form onSubmit={handleCreateWorkspace} className="bg-indigo-50/60 dark:bg-indigo-950/30 p-6 rounded-3xl border border-indigo-100 dark:border-indigo-900 space-y-4 animate-in slide-in-from-top-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
              Provision Additional Workspace
            </h4>
            <button
              type="button"
              onClick={() => setIsCreating(false)}
              className="text-xs font-bold text-slate-400 hover:text-slate-600"
            >
              Cancel
            </button>
          </div>
          <div className="flex gap-3">
            <input
              type="text"
              required
              placeholder="e.g. EU Operations, Staging Environment, Client Portal"
              value={newWorkspaceName}
              onChange={e => setNewWorkspaceName(e.target.value)}
              className="flex-1 px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-bold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-sm transition-all"
            >
              Provision Workspace
            </button>
          </div>
          <p className="text-[11px] text-slate-500">
            Note: Additional workspaces beyond your included tier allowance will adjust your monthly subscription accordingly.
          </p>
        </form>
      )}

      {/* ========================================================================= */}
      {/* SUB-VIEW 1: DETAILED LIST OF ALL ACTIVE ADD-ONS GROUPED BY WORKSPACE      */}
      {/* ========================================================================= */}
      {subView === 'allocated-list' && (
        <div className="space-y-6">
          {/* Workspace-Grouped Breakdown Cards */}
          <div className="grid grid-cols-1 gap-6">
            {workspaces
              .filter(ws => selectedWorkspaceFilter === 'all' || ws.id === selectedWorkspaceFilter)
              .map(ws => {
                const wsAddons = ws.activeAddons || [];
                const wsCost = wsAddons.reduce((sum, a) => sum + (a.price || 0), 0);

                return (
                  <div
                    key={ws.id}
                    className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-5"
                  >
                    {/* Workspace Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-2xl">
                          <Server className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                              {ws.name}
                            </h3>
                            <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                              {ws.isPrimary ? 'Primary Workspace' : 'Expansion Workspace'}
                            </span>
                          </div>
                          <p className="text-[11px] font-mono text-slate-400 mt-0.5">
                            ID: <span className="text-indigo-600 dark:text-indigo-400 font-bold">{ws.id}</span> · Slug: {ws.slug || ws.id}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 self-end sm:self-center">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                          Workspace Add-ons Total:
                        </span>
                        <span className="text-base font-black text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
                          ₹{wsCost} / mo
                        </span>
                      </div>
                    </div>

                    {/* Active Add-ons in this Workspace */}
                    {wsAddons.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {wsAddons.map(addon => {
                          const matchingAddonDef = allAddons.find(a => a.id === addon.addonId);
                          return (
                            <div
                              key={addon.addonId}
                              className="p-5 rounded-2xl border border-indigo-100 dark:border-indigo-900/60 bg-indigo-50/30 dark:bg-indigo-950/20 flex flex-col justify-between gap-3 relative group"
                            >
                              <div>
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                                    <Check className="w-3 h-3" /> Active License
                                  </span>
                                  <span className="text-xs font-black text-slate-900 dark:text-white">
                                    ₹{addon.price} / mo
                                  </span>
                                </div>
                                <h4 className="text-sm font-black text-slate-900 dark:text-white">
                                  {addon.name}
                                </h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed line-clamp-2">
                                  {matchingAddonDef?.description || 'Workspace-dedicated add-on subscription.'}
                                </p>
                              </div>

                              <div className="pt-3 border-t border-indigo-100 dark:border-indigo-900/50 flex items-center justify-between text-[10px]">
                                <span className="text-slate-400 font-medium">
                                  Assigned: {addon.assignedAt ? new Date(addon.assignedAt).toLocaleDateString() : 'Active'}
                                </span>
                                <button
                                  disabled={isPurchasing}
                                  onClick={() => handleToggleAddonForWorkspace(ws.id, {
                                    id: addon.addonId,
                                    name: addon.name,
                                    price: addon.price,
                                    currency: addon.currency || 'INR',
                                    description: '',
                                    isEnabled: true,
                                    isPerWorkspace: true
                                  })}
                                  className="text-rose-600 hover:text-rose-700 dark:text-rose-400 font-bold uppercase tracking-wider flex items-center gap-1 hover:underline cursor-pointer"
                                >
                                  <Trash2 className="w-3 h-3" /> Remove
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-6 px-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 text-center">
                        <p className="text-xs text-slate-400 font-medium">No dedicated add-ons attached to this workspace.</p>
                        <button
                          onClick={() => {
                            setSelectedWorkspaceId(ws.id);
                            setSubView('workspace-detail');
                          }}
                          className="mt-2 text-xs font-black text-indigo-600 hover:underline uppercase tracking-wider inline-flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Add Add-on to {ws.name}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Master Table of All Allocated Add-ons */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <Table className="w-4 h-4 text-indigo-600" />
                Cross-Workspace Add-on Ledger ({filteredAllocations.length})
              </h3>
              <span className="text-xs text-slate-400 font-medium">
                Real-time SubScale sync
              </span>
            </div>

            {filteredAllocations.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-xs">
                No active workspace add-on allocations matching the criteria.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] font-black uppercase tracking-wider text-slate-400">
                      <th className="pb-3 px-3">Add-on Name</th>
                      <th className="pb-3 px-3">Assigned Workspace</th>
                      <th className="pb-3 px-3">Workspace ID</th>
                      <th className="pb-3 px-3">Monthly Price</th>
                      <th className="pb-3 px-3">Assigned Date</th>
                      <th className="pb-3 px-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredAllocations.map((item, idx) => (
                      <tr key={`${item.workspaceId}-${item.addonId}-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <td className="py-3.5 px-3 font-bold text-slate-900 dark:text-white">
                          {item.name}
                        </td>
                        <td className="py-3.5 px-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{item.workspaceName}</span>
                            {item.isPrimary && (
                              <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400">
                                Primary
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3.5 px-3 font-mono text-[11px] text-slate-400">
                          {item.workspaceId}
                        </td>
                        <td className="py-3.5 px-3 font-black text-indigo-600 dark:text-indigo-400">
                          ₹{item.price} / mo
                        </td>
                        <td className="py-3.5 px-3 text-slate-500 font-medium">
                          {item.assignedAt ? new Date(item.assignedAt).toLocaleDateString() : 'Active'}
                        </td>
                        <td className="py-3.5 px-3 text-right">
                          <button
                            disabled={isPurchasing}
                            onClick={() => handleToggleAddonForWorkspace(item.workspaceId, {
                              id: item.addonId,
                              name: item.name,
                              price: item.price,
                              currency: item.currency || 'INR',
                              description: '',
                              isEnabled: true,
                              isPerWorkspace: true
                            })}
                            className="px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300 text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer"
                          >
                            Detach
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-VIEW 2: CROSS-WORKSPACE MATRIX VIEW                                   */}
      {/* ========================================================================= */}
      {subView === 'matrix' && (
        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div>
              <h3 className="text-base font-black text-slate-900 dark:text-white uppercase tracking-wider">
                Cross-Workspace Add-on Allocation Matrix
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Toggle add-ons directly across any workspace instance from this matrix grid.
              </p>
            </div>
            <div className="text-xs text-slate-400 font-medium">
              Click any cell to toggle activation
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="p-4 font-black uppercase text-[10px] tracking-wider text-slate-400 bg-slate-50 dark:bg-slate-800/40 rounded-tl-2xl">
                    Add-on Feature
                  </th>
                  <th className="p-4 font-black uppercase text-[10px] tracking-wider text-slate-400 bg-slate-50 dark:bg-slate-800/40">
                    Monthly Price
                  </th>
                  {workspaces.map((ws, i) => (
                    <th
                      key={ws.id}
                      className={`p-4 font-black text-center text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-800/40 ${
                        i === workspaces.length - 1 ? 'rounded-tr-2xl' : ''
                      }`}
                    >
                      <div className="font-bold">{ws.name}</div>
                      <div className="text-[10px] font-mono text-slate-400 font-normal mt-0.5">
                        {ws.slug || ws.id}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {allAddons.map(addon => (
                  <tr key={addon.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                    <td className="p-4">
                      <div className="font-black text-slate-900 dark:text-white text-xs">{addon.name}</div>
                      <div className="text-[11px] text-slate-400 max-w-xs">{addon.description}</div>
                    </td>
                    <td className="p-4 font-black text-indigo-600 dark:text-indigo-400">
                      ₹{addon.price} / mo
                    </td>
                    {workspaces.map(ws => {
                      const isAssigned = (ws.activeAddons || []).some(a => a.addonId === addon.id);
                      return (
                        <td key={`${ws.id}-${addon.id}`} className="p-4 text-center">
                          <button
                            disabled={isPurchasing}
                            onClick={() => handleToggleAddonForWorkspace(ws.id, addon)}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all inline-flex items-center gap-1.5 cursor-pointer ${
                              isAssigned
                                ? 'bg-emerald-500 text-white shadow-sm hover:bg-rose-600 hover:text-white group'
                                : 'bg-slate-100 hover:bg-indigo-600 hover:text-white text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                            }`}
                          >
                            {isAssigned ? (
                              <>
                                <Check className="w-3.5 h-3.5 group-hover:hidden" />
                                <span className="group-hover:hidden">Active</span>
                                <span className="hidden group-hover:inline">Remove</span>
                              </>
                            ) : (
                              <>
                                <Plus className="w-3.5 h-3.5" />
                                <span>Add</span>
                              </>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-VIEW 3: MANAGE INDIVIDUAL WORKSPACE & CATALOG VIEW                    */}
      {/* ========================================================================= */}
      {subView === 'workspace-detail' && (
        <div className="space-y-6">
          {/* Workspace Selector Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {workspaces.map((ws) => {
              const isSelected = selectedWorkspace?.id === ws.id;
              const totalAddonCost = (ws.activeAddons || []).reduce((sum, a) => sum + (a.price || 0), 0);
              return (
                <div
                  key={ws.id}
                  onClick={() => setSelectedWorkspaceId(ws.id)}
                  className={`p-6 rounded-3xl border cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-white dark:bg-slate-900 border-indigo-600 dark:border-indigo-500 ring-2 ring-indigo-600/20 shadow-md'
                      : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                      {ws.isPrimary ? 'Primary Workspace' : 'Expansion Workspace'}
                    </span>
                    <span className="text-xs font-black text-indigo-600 dark:text-indigo-400">
                      ₹{totalAddonCost}/mo add-ons
                    </span>
                  </div>
                  <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight mb-1">
                    {ws.name}
                  </h3>
                  <p className="text-[11px] text-slate-400 font-mono mb-4">
                    ID: {ws.id}
                  </p>

                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                    <span className="text-slate-500">
                      {(ws.activeAddons || []).length} active {(ws.activeAddons || []).length === 1 ? 'add-on' : 'add-ons'}
                    </span>
                    <span className={`text-[10px] font-black uppercase tracking-wider ${isSelected ? 'text-indigo-600' : 'text-slate-400'}`}>
                      {isSelected ? 'Selected' : 'Select'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected Workspace Add-ons Catalog */}
          {selectedWorkspace && (
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                      Add-on Modules for <span className="text-indigo-600 dark:text-indigo-400">{selectedWorkspace.name}</span>
                    </h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Toggle features on or off. Each purchased add-on applies exclusively to <strong>{selectedWorkspace.name}</strong>.
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block">Workspace Add-on Total</span>
                  <span className="text-xl font-black text-slate-900 dark:text-white">
                    ₹{(selectedWorkspace.activeAddons || []).reduce((sum, a) => sum + (a.price || 0), 0)} / mo
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allAddons.map((addon) => {
                  const isAttached = (selectedWorkspace.activeAddons || []).some(a => a.addonId === addon.id);
                  return (
                    <div
                      key={addon.id}
                      className={`p-5 rounded-2xl border transition-all flex flex-col justify-between gap-4 ${
                        isAttached
                          ? 'bg-indigo-50/40 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800/80'
                          : 'bg-slate-50/50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-800'
                      }`}
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-black text-slate-900 dark:text-white">
                            {addon.name}
                          </h4>
                          <span className="text-xs font-black text-indigo-600 dark:text-indigo-400">
                            ₹{addon.price} / mo
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                          {addon.description}
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                          Target: {selectedWorkspace.slug || selectedWorkspace.name}
                        </span>
                        <button
                          disabled={isPurchasing}
                          onClick={() => handleToggleAddonForWorkspace(selectedWorkspace.id, addon)}
                          className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer ${
                            isAttached
                              ? 'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-300'
                              : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'
                          }`}
                        >
                          {isAttached ? (
                            <>Remove Add-on</>
                          ) : (
                            <>
                              <Plus className="w-3.5 h-3.5" />
                              Add to {selectedWorkspace.name}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
